/**
 * Song detail (F1 — music capability): lyrics + CHORDS/NOTES BY
 * MUSICIAN FUNCTION (guitar, bass, voice…) with on-the-fly transposition to
 * any key. The musician sees exactly what their `songs` grant syncs; writing
 * requires the group's `songs:write`.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  getSongPartsQuery,
  getSongsQuery,
  MUSICAL_KEYS,
  patchRowVerified,
  prefersFlats,
  queryKeys,
  semitonesBetween,
  transposeContent,
} from "@agape/shared";
import {
  eq,
  song_part,
  type SongPartRecord,
  type SongRecord,
  song as songTable,
} from "@agape/powersync";
import { Music, Pencil, Plus, Trash2 } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { Field, FormDialog, TextArea } from "./form";
import { newId, nowIso, useDomainQuery, useGrants, useWriter } from "../use-domain";

export function SongDetailPage(props: { songId: string }) {
  const { t } = useI18n();
  const write = useWriter();
  const grants = useGrants();

  const songs = useDomainQuery<SongRecord>(
    (uid) => queryKeys.domain.songs(uid),
    (db) => getSongsQuery(db),
  );
  const parts = useDomainQuery<SongPartRecord>(
    (uid) => queryKeys.domain.songParts(uid),
    (db) => getSongPartsQuery(db),
  );

  const song = createMemo(() => (songs.data ?? []).find((s) => s.id === props.songId));
  const myParts = createMemo(() => (parts.data ?? []).filter((p) => p.song_id === props.songId));
  const canWrite = () => grants.can([song()?.group_id, song()?.church_id], "songs", "write");

  // DISPLAY key (on-the-fly transposition, does not mutate the content).
  const [viewKey, setViewKey] = createSignal<string>("");
  const semitones = createMemo(() => {
    const from = song()?.song_key;
    const to = viewKey();
    if (!from || !to || from === to) {
      return 0;
    }
    return semitonesBetween(from, to);
  });
  const rendered = (content: string | null) => {
    if (!content) {
      return "";
    }
    const st = semitones();
    if (st === 0) {
      return content;
    }
    return transposeContent(content, st, prefersFlats(viewKey()));
  };

  // ── Edit lyrics/metadata ──────────────────────────────────────────────────
  const [editOpen, setEditOpen] = createSignal(false);
  const [title, setTitle] = createSignal("");
  const [author, setAuthor] = createSignal("");
  const [key, setKey] = createSignal("C");
  const [tempo, setTempo] = createSignal("");
  const [tags, setTags] = createSignal("");
  const [lyrics, setLyrics] = createSignal("");
  const openEdit = () => {
    const s = song();
    if (!s) {
      return;
    }
    setTitle(s.title ?? "");
    setAuthor(s.author ?? "");
    setKey(s.song_key ?? "C");
    setTempo(s.tempo == null ? "" : String(s.tempo));
    setTags(s.tags ?? "");
    setLyrics(s.lyrics ?? "");
    setEditOpen(true);
  };
  const saveEdit = async () => {
    await patchRowVerified(write(), songTable, songTable.id, props.songId, {
      title: title().trim(),
      author: author().trim() || null,
      song_key: key(),
      tempo: tempo() ? Number(tempo()) : null,
      tags: tags().trim() || null,
      lyrics: lyrics() || null,
    });
    setEditOpen(false);
    showToast(t(msg`Song updated`), "success");
  };

  // ── Parts (chords by function) ────────────────────────────────────────────
  const [partOpen, setPartOpen] = createSignal(false);
  const [partId, setPartId] = createSignal<string | null>(null);
  const [instrument, setInstrument] = createSignal("");
  const [content, setContent] = createSignal("");
  const openPart = (p?: SongPartRecord) => {
    setPartId(p?.id ?? null);
    setInstrument(p?.instrument ?? "");
    setContent(p?.content ?? "");
    setPartOpen(true);
  };
  const savePart = async () => {
    if (!instrument().trim()) {
      return;
    }
    const id = partId();
    if (id) {
      await patchRowVerified(write(), song_part, song_part.id, id, {
        instrument: instrument().trim(),
        content: content() || null,
      });
    } else {
      await write()
        .insert(song_part)
        .values({
          id: newId(),
          song_id: props.songId,
          church_id: song()?.church_id,
          group_id: song()?.group_id,
          instrument: instrument().trim(),
          content: content() || null,
          created_at: nowIso(),
          updated_at: nowIso(),
        });
    }
    setPartOpen(false);
    showToast(t(msg`Part saved`), "success");
  };
  const removePart = async (id: string) => {
    await write().delete(song_part).where(eq(song_part.id, id));
  };

  return (
    <div class="space-y-6">
      <PageSubHeader title={song()?.title ?? t(msg`Song`)} backTo="/songs" />
      <Show
        when={song()}
        fallback={
          <p class="text-muted-foreground">{t(msg`This song is not synced to your account.`)}</p>
        }
      >
        {(s) => (
          <>
            <div class="flex flex-wrap items-center justify-between gap-3">
              <p class="text-sm text-muted-foreground" data-testid="song-meta">
                {[
                  s().author,
                  (() => {
                    const songKey = s().song_key;
                    return songKey ? t(msg`Key: ${songKey}`) : null;
                  })(),
                  s().tempo ? `${s().tempo} bpm` : null,
                  s().tags,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              <div class="flex items-center gap-2">
                <span class="text-xs uppercase tracking-wide text-muted-foreground">
                  {t(msg`View in`)}
                </span>
                <div class="w-24">
                  <Select
                    options={[
                      { value: "", label: s().song_key ?? t(msg`Original`) },
                      ...MUSICAL_KEYS.filter((k) => k !== s().song_key).map((k) => ({
                        value: k,
                        label: k,
                      })),
                    ]}
                    value={viewKey()}
                    onChange={setViewKey}
                    aria-label={t(msg`View in key`)}
                  />
                </div>
                <Show when={canWrite()}>
                  <Button size="sm" variant="secondary" data-testid="song-edit" onClick={openEdit}>
                    <Pencil size={14} /> {t(msg`Edit`)}
                  </Button>
                </Show>
              </div>
            </div>

            <Show when={s().lyrics}>
              <Card data-testid="song-lyrics">
                <CardContent class="py-4">
                  <pre class="whitespace-pre-wrap font-mono text-sm">{rendered(s().lyrics)}</pre>
                </CardContent>
              </Card>
            </Show>

            <section class="space-y-2">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {t(msg`Parts by musician function`)}
                </h3>
                <Show when={canWrite()}>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid="song-part-add"
                    onClick={() => openPart()}
                  >
                    <Plus size={14} /> {t(msg`Add part`)}
                  </Button>
                </Show>
              </div>
              <Show
                when={myParts().length > 0}
                fallback={
                  <p class="text-sm text-muted-foreground" data-testid="song-parts-empty">
                    {t(msg`No parts yet — add chords or notes per function.`)}
                  </p>
                }
              >
                <For each={myParts()}>
                  {(p) => (
                    <Card data-testid="song-part-item" data-instrument={p.instrument}>
                      <CardContent class="space-y-2 py-3">
                        <div class="flex items-center justify-between gap-3">
                          <p class="text-sm font-semibold">{p.instrument}</p>
                          <Show when={canWrite()}>
                            <div class="flex gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                aria-label={t(msg`Edit part`)}
                                data-testid="song-part-edit"
                                onClick={() => openPart(p)}
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                aria-label={t(msg`Remove part`)}
                                onClick={() => void removePart(p.id)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </Show>
                        </div>
                        <pre
                          class="whitespace-pre-wrap font-mono text-sm"
                          data-testid="song-part-content"
                        >
                          {rendered(p.content)}
                        </pre>
                      </CardContent>
                    </Card>
                  )}
                </For>
              </Show>
            </section>
          </>
        )}
      </Show>

      {/* ── Dialogs ─────────────────────────────────────────────────────────── */}
      <FormDialog
        open={editOpen()}
        onOpenChange={setEditOpen}
        title={t(msg`Edit song`)}
        icon={<Music size={20} />}
        submitTestId="song-edit-submit"
        disabled={!title().trim()}
        onSubmit={saveEdit}
      >
        <Field id="se-title" label={t(msg`Title`)} value={title()} onInput={setTitle} />
        <div class="grid grid-cols-3 gap-3">
          <Field id="se-key" label={t(msg`Key`)}>
            <Select
              options={MUSICAL_KEYS.map((k) => ({ value: k, label: k }))}
              value={key()}
              onChange={setKey}
              aria-label={t(msg`Key`)}
            />
          </Field>
          <Field
            id="se-tempo"
            label={t(msg`Tempo (bpm)`)}
            type="number"
            value={tempo()}
            onInput={setTempo}
          />
          <Field id="se-author" label={t(msg`Author`)} value={author()} onInput={setAuthor} />
        </div>
        <Field id="se-tags" label={t(msg`Tags`)} value={tags()} onInput={setTags} />
        <Field id="se-lyrics" label={t(msg`Lyrics`)}>
          <TextArea
            id="se-lyrics"
            rows={8}
            value={lyrics()}
            onInput={setLyrics}
            testId="song-lyrics-input"
          />
        </Field>
      </FormDialog>

      <FormDialog
        open={partOpen()}
        onOpenChange={setPartOpen}
        title={partId() ? t(msg`Edit part`) : t(msg`Add part`)}
        icon={<Music size={20} />}
        submitTestId="song-part-submit"
        disabled={!instrument().trim()}
        onSubmit={savePart}
      >
        <Field
          id="sp-instrument"
          label={t(msg`Musician function`)}
          value={instrument()}
          onInput={setInstrument}
          placeholder={t(msg`Guitar, bass, keys, voice…`)}
          testId="song-part-instrument"
        />
        <Field id="sp-content" label={t(msg`Chords / notes`)}>
          <TextArea
            id="sp-content"
            rows={8}
            value={content()}
            onInput={setContent}
            // oxlint-disable-next-line lingui/no-unlocalized-strings -- chord example, not UI
            placeholder={"C      G      Am     F\n…"}
            testId="song-part-content-input"
          />
        </Field>
      </FormDialog>
    </div>
  );
}
